import { ExplainerPanel } from "./ExplainerPanel.tsx";
import { theme } from "../theme.ts";

/**
 * Artifact-style Forecast explainer. Placeholders from the artifact
 * (PER_SKU_MIN_YTD=5, MULT_MIN=0.25, MULT_MAX=4.0) inlined.
 */
export function ForecastExplainer() {
  return (
    <ExplainerPanel summary="How the Forecast view is computed — data sources, filters, and sign conventions">
      <p style={{ marginTop: 0 }}>
        Demand comes from <b>NetSuite</b> and on-water from <b>Synkio</b>,
        pulled by cron into D1 (<code>demand_daily</code> →{" "}
        <code>demand_monthly</code>, <code>inventory_current</code>,{" "}
        <code>open_pos</code>, <code>on_water</code>), then materialized nightly
        into <code>forecast_current</code> and served to this page in one
        indexed SELECT.
      </p>
      <div
        style={{
          background: theme.color.surfaceHover,
          padding: "10px 14px",
          margin: "10px 0",
          borderRadius: 6,
          fontSize: 12.5,
          lineHeight: 1.6,
        }}
      >
        <b>The method, in one line:</b> a <b>seasonal-analog average</b> (what
        this SKU actually sold in this same 90-day window over the last 3 years)
        <b> scaled by a trailing-12-month YoY trend</b>. It is deliberately{" "}
        <i>not</i> a statistical model — no regression, exponential smoothing,
        ARIMA or ML, and no confidence interval. The pay-off is that every
        number in a row reconciles by hand from the columns next to it.
      </div>

      <div
        style={{
          background: theme.color.brandFaint,
          borderLeft: `3px solid ${theme.color.brand}`,
          padding: "10px 14px",
          margin: "10px 0",
          borderRadius: 4,
          fontSize: 12.5,
          lineHeight: 1.6,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>
          ⟳ Data refresh schedule
        </div>
        <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
          <li>
            <b>Forecast recompute</b> — the whole forecast (baseline, growth
            multiplier, months of cover) is re-materialized{" "}
            <b>once nightly, at 6&nbsp;AM&nbsp;UTC (≈11&nbsp;PM Pacific)</b>.
            That's also when the <b>rolling 90-day window rolls forward a
            day</b>, so the model output is at most a day old.
          </li>
          <li>
            <b>Supply refreshes faster</b> — inventory (Comp Avail), open POs
            (Comp PO) and on-water (Synkio) all reload <b>hourly</b>, and the{" "}
            <b>Total Supply / Need to order columns pick that up immediately</b>,
            because the route does that arithmetic per request. Only the demand
            side (baseline, growth, forecast) waits for the nightly recompute.
          </li>
          <li>
            <b>SKU catalog + BOM edges</b> — refreshed nightly (slowly-changing).
          </li>
        </ul>
        <div style={{ marginTop: 6, color: theme.color.brandInk }}>
          Net effect: between nightly runs the Forecast reflects yesterday's
          model even though inventory has moved during the day. For the
          up-to-the-hour supply-vs-burn picture, use the <b>Monitor</b> tab.
        </div>
      </div>

      <h4>Supply columns</h4>
      <ul>
        <li>
          <b>Comp Avail</b> — <code>aggregateItemLocation.quantityavailable</code>{" "}
          for the SKU itself at Loc 15 + 64 (Jurupa + Texas).{" "}
          <b>Loc 65, Gold Crest Texas, is excluded</b> — that is a separate
          legal entity (NetSuite subsidiary 4), so its stock is not March
          supply. Monitor has its own Gold Crest view; Forecast is
          March&nbsp;Products only.
        </li>
        <li>
          <b>In built umbrellas</b> (Frames and Covers only) — frames and covers
          already assembled into umbrellas in stock at Loc 15 + 64. NetSuite
          counts those units as stock of the umbrella, so Comp Avail does not
          show them. <b>Reference only:</b> it is not added to Total Supply or
          To Order, because it only helps if those exact umbrellas sell — a
          logo or bespoke build made for one customer will not. Hover the
          number for the umbrellas holding them. Flagged rows also show it under
          the SKU, so you can decide whether the flag still stands. Fabric and
          trim are not counted: once sewn into a cover they cannot be
          recovered. Refreshed hourly.
        </li>
        <li>
          <b>Comp PO</b> — open purchase order lines for the SKU at Loc 15 + 64:{" "}
          <code>SUM(quantity − quantityshiprecv)</code> across open PO statuses
          (B = Pending Receipt, D = Partially Received, F = Pending Bill).{" "}
          <b>Work Orders are not included.</b>
        </li>
        <li>
          <b>On Water</b> — in-transit container quantity from <b>Synkio</b>{" "}
          (active, not-yet-arrived shipments), refreshed <b>hourly</b>, with
          the earliest ETA beside it. <b>Most of it is a slice of Comp PO, not
          an addition to it</b> — see the note below. A <code>*</code> marks
          rows that include units with no PO allocation (the part that <i>is</i>{" "}
          added). Shows <code>—</code> when nothing is in transit.
        </li>
        <li>
          <b>PO arrives</b> — the earliest{" "}
          <code>expected_receipt_date</code> across the SKU's open POs, with
          days away beside it. That field is{" "}
          <code>transactionline.expectedreceiptdate</code> in NetSuite — set
          per PO line, typically by the buyer at order time, and{" "}
          <b>not automatically revised when a shipment slips</b>. Median lead
          time on current open POs is about 124 days from PO date.
          <br />
          <b>How much to trust it:</b> checked against 12 months of actual
          receipts (2,371 PO lines), <b>70% of received volume never had the
          date set</b> — NetSuite defaults it to the PO date. Even where a buyer
          did set it, only about <b>1 line in 5 arrived within a week</b> of it;
          a third came more than two weeks early and a third more than a month
          late. Read it as the supplier's last stated intent, not a tracked ETA.
          <br />
          <b>No ETA</b> — stock is on order but the expected date was never set
          (it equals the PO date), so the arrival is unknown. Earlier versions
          showed these as late, which overstated lateness about threefold. Rows
          mixing both show the dated ETA with <code>+N no ETA</code> beneath.
          <br />
          Red in two cases: the date <b>lands after the 90-day window</b> (Total
          Supply counts that PO in full regardless of when it turns up), or a{" "}
          <i>set</i> date has <b>already passed</b>, shown as{" "}
          <code>(N d late)</code>. <code>—</code> when nothing is on order.
        </li>
        <li>
          <b>Days exposed</b> — how long the shelf is empty before that
          delivery lands: <code>days until arrival − days of cover on hand</code>.
          This is the number the expedite decision turns on. <code>—</code> when
          on-hand lasts until the delivery, or when nothing is inbound. Reads{" "}
          <b>PO late</b> when a set delivery date has passed, and{" "}
          <b>No ETA</b> when no open PO has a date at all — in both cases the
          exposure is open-ended, not zero. Given how loosely these dates track
          reality (above), treat it as a rough guide for which suppliers to
          call, not a count of days.
        </li>
        <li>
          <b>Total supply</b> = Comp Avail + Comp PO + on-water that isn't on
          any PO. Bolded blue cell. <b>Timing-blind by construction</b> — see
          PO arrives above.
        </li>
      </ul>

      <h4>Row tags</h4>
      <ul>
        <li>
          <span style={{ background: theme.color.warningFaint, color: theme.color.warningText, border: `1px solid ${theme.color.warningBorder}`, padding: "1px 6px", borderRadius: 999, fontSize: 10, fontWeight: 600 }}>expedite?</span>{" "}
          — <b>on-hand stock is below the 90-day forecast, but units are
          already on order.</b> The trigger deliberately ignores Comp PO and
          On Water: a container arriving in five weeks does not stop the shelf
          being empty today, and the lever is usually to airfreight what is
          already coming rather than to buy more. Read Days exposed to decide.
          Need to order stays at <code>—</code> for these, because the stock is
          bought.
        </li>
        <li>
          <span style={{ background: theme.color.dangerFaint, color: theme.color.dangerText, border: `1px solid ${theme.color.dangerBorder}`, padding: "1px 6px", borderRadius: 999, fontSize: 10, fontWeight: 600 }}>order</span>{" "}
          — on-hand is below the 90-day forecast and <b>nothing is on
          order</b>. Someone has to raise a PO; Need to order says how much.
        </li>
        <li>
          <b>Low on-hand only</b> (checkbox) filters to just those two, with
          the counts beside it.
        </li>
      </ul>

      <div
        style={{
          background: theme.color.warningFaint,
          border: `1px solid ${theme.color.warningBorder}`,
          padding: "10px 14px",
          borderRadius: 8,
          fontSize: 12.5,
          lineHeight: 1.6,
          color: theme.color.warningText,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>
          ★ Why On Water is mostly <i>not</i> added to Total Supply
        </div>
        <p style={{ margin: "4px 0" }}>
          Containers normally ship <b>against open NetSuite POs</b> (the Synkio
          line carries a <code>purchase_order_item_id</code>), and NetSuite only
          marks a PO received when it <b>physically arrives</b>. So while a
          container is at sea, those units sit in <b>Comp PO</b> and{" "}
          <b>On Water</b> at once — two systems describing the same units, not
          two lanes of supply.
        </p>
        <p style={{ margin: "4px 0" }}>
          Example: PO 14634 shows <code>ordered 680, received 0, remaining
          680</code> for ALTO908-302, and Synkio has the <i>same</i> 680 on
          container CA-26092. Adding them would claim 1,360 units we don’t
          have. Allocated on-water can never exceed the PO it belongs to.
        </p>
        <p style={{ margin: "4px 0" }}>
          <b>The exception (marked with a <code>*</code>):</b> some containers
          are entered <b>without</b> PO allocation — CA-26087 says outright
          “ENTERED UNALLOCATED — needs manual PO allocation”. No open PO
          represents those units, so ignoring them would tell you to re-order
          stock already at sea. That slice <b>is</b> added to Total Supply.
        </p>
        <p style={{ margin: "4px 0 0" }}>
          Otherwise On Water is here for <b>visibility and timing</b>, not
          quantity — and its ETA is far better than the PO’s. That same PO’s
          NetSuite <code>expected_receipt_date</code> claims <b>2026-11-25</b>{" "}
          while the container actually lands <b>2026-07-10</b>.
        </p>
      </div>

      <h4>Demand — the multi-pass UNION</h4>
      <p>
        Each backfilled month has two passes for most SKUs (three for fabrics):
      </p>
      <ul>
        <li>
          <b>Pass 1 — Direct sales:</b> line item IS one of the ~1,000 tracked
          SKUs.
        </li>
        <li>
          <b>Pass 2 — L1 BOM consumption:</b> line item is an FG whose BOM
          contains our SKU at L1. Demand ={" "}
          <code>(−1 × line_qty) × bom_ratio</code>. Uses BOM_UNION of legacy{" "}
          <code>assemblyItemMember</code> + newer{" "}
          <code>bomrevisioncomponentmember</code>, with a{" "}
          <b>LEFT&nbsp;JOIN anti-join</b> (<code>tlc.id IS NULL</code>) so we
          don't double-count invoices that itemize both the FG and its BOM
          children. (An internal <code>NOT EXISTS</code> also dedups the
          BOM_UNION itself, so the newer bomrevision table only contributes
          when the legacy table has no rows for that assembly — that's a
          separate guard.)
        </li>
        <li>
          <b>Pass 3 — L2 fabric (fabrics only):</b> for fabric SKUs a third
          pass walks <code>FG → cover assembly → fabric</code>. Demand ={" "}
          <code>(−1 × line_qty) × cover_qty × fabric_qty_per_cover</code>. This
          lands in <code>l2_fabric_qty</code> and <b>is included</b> in the
          forecast baseline (baseline sums Direct + L1 + L2).
        </li>
      </ul>

      <h4>Lead time: what "Need to order" is judged against</h4>
      <ul>
        <li>
          The forecast looks <b>90 days</b> ahead for monitoring. But{" "}
          <b>Need to order</b> and the <b>order / expedite?</b> tags are judged
          over each SKU's <b>lead time</b> — how long from ordering to usable
          stock: <b>frames and bases 90 days</b> (overseas), <b>fabric 14</b>{" "}
          (local), <b>covers 3</b> off season and <b>7 March–August</b> (cut
          and sewn in-house: 2–3 days, 5–7 at peak; the fabric they need is
          tracked separately). Admins adjust these on the Admin page. A lead time set
          on the item in NetSuite overrides its category.
        </li>
        <li>
          Ordering is on demand, so stock on hand plus everything on order only
          has to last the lead time: the app checks nightly, and an order placed
          the day a gap appears lands in time. Judging fabric against 90 days of
          demand flagged it about six times too early.
        </li>
        <li>
          The <b>Lead-time demand</b> column shows the forecast over that
          window (e.g. "412 14d").
        </li>
        <li>
          <b>Safety buffer</b> (Frames and Bases by default) is extra stock on
          top of lead-time demand, so a busier-than-forecast stretch doesn't
          empty the shelf before a new order can land. Each SKU's buffer is
          sized from how wrong its own past 90-day forecasts were, with a
          multiplier calibrated so it would actually have hit the target on
          past windows. The target and what it means — no stockout in 95% of
          windows, or 95% of demand shipped from stock — are set by admins on
          the Admin page. <b>Need to order</b> = lead-time demand + safety
          buffer − total supply.
        </li>
      </ul>

      <h4>Which model each tab uses</h4>
      <ul>
        <li>
          <b>Frames and Covers: top-down split.</b> The whole category's
          90-day forecast is worked out with the formula below, then shared out
          by each SKU's share of the category's last 12 months. In the backtest
          (55 windows, volume-weighted error) it beat the per-SKU formula for
          Frames 30% vs 36% and Covers 34% vs 40%. These rows
          carry a small <b>SPLIT</b> tag; hover for the SKU's share and what
          the formula alone would have said.
        </li>
        <li>
          <b>Bases: top-down split × category pace</b> (tag{" "}
          <b>SPLIT·PACE</b>). The same split, then nudged halfway toward how
          bases have actually sold over the last 28 days against forecast
          (limited to ±50%; one-off large orders capped). Bases have been
          trending down, which the plain split kept missing: in-season error
          27% vs 29%, and the overshoot cut from +12% to +3%.
        </li>
        <li>
          <b>Fabric: top-down split with cancelled orders added back</b>
          (tag <b>SPLIT+</b>), on the <b>Fabric</b> tab. Fabric is counted in{" "}
          <b>yards</b>, not units, so every quantity on that tab is yards; it
          lists what needs ordering first, then the busiest fabrics. Orders customers placed that were then cancelled
          or closed unshipped count as demand in the history — so a fabric that
          lost orders to shortages is not forecast lower for it. Backtest error
          21% vs 30% for the formula, best on every yardstick.
        </li>
        <li>
          Baseline and Growth stay visible on every row for reference but no
          longer drive the number. The Accuracy page keeps scoring every model
          each week, so a category can change model when the evidence does.
        </li>
      </ul>

      <h4>Forecast formula (per SKU, rolling 90 days)</h4>
      <p
        style={{
          fontFamily: "'SF Mono',Menlo,Consolas,monospace",
          background: theme.color.surfaceHover,
          padding: "10px 14px",
          borderRadius: 4,
          fontSize: 12.5,
        }}
      >
        window = [today, today + 89 days]
        <br />
        Forecast(SKU) = avg(demand in that window 1, 2 and 3 yrs ago) ×
        growth(SKU)
      </p>
      <p>
        <b>The horizon is a true rolling 90 days</b>, not three whole
        calendar months — it starts <i>today</i> and the exact window is shown
        above the table. Each prior-year figure is that same window shifted
        back a year (day-level, from <code>demand_daily</code>), so the
        comparison is like-for-like and never re-forecasts days that have
        already elapsed. Every demand number in the table — the three year
        columns, Baseline and the forecast — is a <b>90-day total</b>.
      </p>
      <ul>
        <li>
          <b>
            Baseline (90d) — the pre-growth demand the forecast starts from
          </b>
          . It is what the SKU sold in this same 90-day stretch historically,
          before any trend adjustment. Reading left to right, the three{" "}
          <b>“YYYY (90d)” columns</b> are the raw inputs — actual demand in the
          same window 1, 2 and 3 years ago — and <b>Baseline</b> is simply their{" "}
          <b>average</b>: <code>(yr-3 + yr-2 + yr-1) ÷ 3</code>.
          <div style={{ marginTop: 6 }}>
            Averaging three years smooths single-year noise: a one-off supply
            outage or a jumbo order in any one year can't dominate. (Hover
            Baseline to see how many years it actually averaged — it uses fewer
            than 3 only where demand history doesn't reach back far enough,
            rather than averaging in a year of zeroes.)
          </div>
          <div style={{ marginTop: 6 }}>
            <b>Watch for years that disagree.</b> When the inputs are far apart,
            the average deliberately damps the spread. If the newest year is a
            real ramp, the baseline <i>understates</i> it; if it was a one-off,
            the baseline correctly pulls it down. The growth multiplier below is
            what's meant to correct a genuine trend.
          </div>
        </li>
        <li>
          <b>Per-SKU trailing-12-month (TTM) YoY growth multiplier</b> —{" "}
          <code>
            demand in the last 12 complete months ÷ demand in the 12 before
            that
          </code>
          . Each month is compared with the same month a year earlier, so both
          sides span a full year and seasonality cancels — what's left is the
          SKU's actual trend.
          <div style={{ marginTop: 6 }}>
            <b>Why TTM and not fiscal-year-to-date</b> (which this used to
            be): March's FY starts 1 July, so through July the FY-YTD window
            contains <i>no completed months</i> — the ratio was undefined and{" "}
            <b>every SKU silently fell back to 1.0</b>, i.e. no trend at all,
            precisely in peak season. TTM is always a full 12 months, so it
            never has that cliff, and its larger totals keep most SKUs above
            the 5-unit floor instead of falling back.
          </div>
          <div style={{ marginTop: 6 }}>
            <b>Why not a shorter (e.g. 90-day) trailing window?</b> Backtested
            against the known Jul 16 – Oct 13 2025 result: <b>TTM came within
            +0.24%</b> of actual, no-growth was +6.4% out, and a trailing-90-day
            window was <b>−14.1%</b> out — a soft spring didn't carry into
            summer, so the short window over-corrected. TTM's weakness is the
            flip side: it <i>lags</i> a genuine inflection by design.
          </div>
        </li>
        <li>
          <b>Fallback to aggregate growth</b> — if either side of the TTM ratio
          is below <code>5</code> units, the SKU uses the all-SKUs aggregate
          growth instead of its own. Per-SKU ratios on low-volume SKUs are noisy
          and produce wild forecasts. Marked with <code>*</code> next to the
          growth % in the table.
        </li>
        <li>
          <b>Want to see the forecast without any trend applied?</b> You already
          are — the <b>Baseline = no-growth fcst</b> column <i>is</i> the
          forecast with growth forced to ×1.0. So the row reads left to right as{" "}
          <code>Baseline (no growth) × Growth = 90-day forecast</code>. Hover
          the Growth cell to see how many units the multiplier moved it.
        </li>
        <li>
          <b>Multiplier cap</b> — clamped to <code>[0.25×, 4×]</code>. Protects
          against absurd swings.
        </li>
      </ul>

      <h4>Sign convention (the magic of −1 × tl.quantity)</h4>
      <p>
        NetSuite stores <code>tl.quantity</code> with opposite signs for
        invoices and credit memos:
      </p>
      <ul>
        <li>
          Invoices: <code>tl.quantity = −1</code> (one unit shipped) →{" "}
          <code>−1 × −1 = +1</code> adds to demand
        </li>
        <li>
          Credit memos: <code>tl.quantity = +1</code> (one unit returned) →{" "}
          <code>−1 × +1 = −1</code> subtracts from demand
        </li>
      </ul>
      <p>
        So one expression nets returns out without any <code>CASE WHEN</code>{" "}
        branch. A SKU with heavy returns will show lower Total Demand than its
        gross sales.
      </p>

      <h4>How a customer transaction becomes per-SKU demand</h4>
      <p>
        Same logic for invoices and credit memos — only the sign differs.
      </p>
      <ol>
        <li>
          Customer places an order. NetSuite writes a row to{" "}
          <code>transaction</code> — type <code>CustInvc</code> (invoice),{" "}
          <code>CashSale</code>, or <code>CustCred</code> (credit memo / return).
        </li>
        <li>
          Each transaction has one or more rows in{" "}
          <code>transactionline</code>. Each line points at an item (could be
          an FG, a frame, a cover assembly, fabric, etc.).
        </li>
        <li>
          Our query inspects each line in <b>two passes</b>:
          <div style={{ marginTop: 6 }}>
            <b>Pass 1 — Direct sale.</b> Is <code>tl.item</code> itself one of
            the ~1,000 tracked SKUs? If yes, count <code>−1 × tl.quantity</code>{" "}
            directly as demand for that SKU.
          </div>
          <div style={{ marginTop: 4 }}>
            <b>Pass 2 — BOM expansion.</b> Otherwise, look up{" "}
            <code>tl.item</code> as a parent in the BOM_UNION. For each L1
            child of that parent, ask: is the child one of our tracked SKUs?
            If yes, count <code>(−1 × tl.quantity) × bom_qty</code> as demand
            for the child SKU.
          </div>
        </li>
        <li>
          Group all matching rows by <code>(sku, day)</code> and sum into{" "}
          <code>demand_daily</code>, rolled up to <code>demand_monthly</code>.
          The nightly job then materializes <code>forecast_current</code> — the
          90-day baseline reads <code>demand_daily</code> (the rolling window
          rarely lands on month boundaries), while the TTM growth ratio reads{" "}
          <code>demand_monthly</code>, since TTM is by definition whole months.
        </li>
      </ol>

      <h4>Three worked examples</h4>
      <div
        style={{
          background: theme.color.surfaceSubtle,
          padding: "10px 14px",
          borderRadius: 4,
          marginTop: 8,
          borderLeft: `3px solid ${theme.color.success}`,
          fontSize: 12,
        }}
      >
        <b>Example A — Customer buys 2 finished umbrellas (FG sale)</b>
        <div style={{ fontFamily: "monospace", marginTop: 4 }}>
          transaction.type = 'CustInvc'
          <br />
          transactionline.item = ATA908117-5403 (a finished umbrella FG)
          <br />
          transactionline.quantity = −2 (NS sign convention for invoices)
          <br />
          assemblyItemMember(ATA908117-5403) = [ ATA908-117 (frame, qty=1),
          C908-5403-SWV (cover, qty=1) ]
        </div>
        <div style={{ marginTop: 6 }}>
          Pass 1: ATA908117-5403 isn't a tracked SKU → no match.
          <br />
          Pass 2 BOM expansion finds two L1 children, both tracked:
          <br />→ <b>ATA908-117 demand += (−1 × −2) × 1 = +2 units</b>
          <br />→ <b>C908-5403-SWV demand += (−1 × −2) × 1 = +2 units</b>
        </div>
      </div>

      <div
        style={{
          background: theme.color.surfaceSubtle,
          padding: "10px 14px",
          borderRadius: 4,
          marginTop: 8,
          borderLeft: `3px solid ${theme.color.warning}`,
          fontSize: 12,
        }}
      >
        <b>Example B — Customer returns 1 finished umbrella (credit memo)</b>
        <div style={{ fontFamily: "monospace", marginTop: 4 }}>
          transaction.type = 'CustCred'
          <br />
          transactionline.quantity = +1 (opposite sign)
        </div>
        <div style={{ marginTop: 6 }}>
          The <code>−1 × tl.quantity</code> trick automatically negates returns:
          <br />→ ATA908-117 demand += (−1 × +1) × 1 = <b>−1 unit</b>
          <br />→ C908-5403-SWV demand += (−1 × +1) × 1 = <b>−1 unit</b>
        </div>
      </div>

      <div
        style={{
          background: theme.color.surfaceSubtle,
          padding: "10px 14px",
          borderRadius: 4,
          marginTop: 8,
          borderLeft: `3px solid ${theme.color.brand}`,
          fontSize: 12,
        }}
      >
        <b>Example C — Customer buys a loose frame (replacement part)</b>
        <div style={{ fontFamily: "monospace", marginTop: 4 }}>
          transactionline.item = ATA908-117 (the frame itself, not an FG)
          <br />
          transactionline.quantity = −1
        </div>
        <div style={{ marginTop: 6 }}>
          Pass 1: ATA908-117 IS in tracked list →{" "}
          <b>demand += −1 × −1 = +1 unit</b> directly.
          <br />
          Pass 2: ATA908-117 has no entry as a parent in{" "}
          <code>assemblyItemMember</code> (it's a leaf) → no additional
          match. No double-counting.
        </div>
      </div>

      <h4 style={{ marginTop: 16 }}>Filters applied to demand queries</h4>
      <ul style={{ fontFamily: "monospace", fontSize: 12 }}>
        <li>
          t.type IN ('CustInvc','CashSale','CustCred') — invoices, cash sales,
          returns
        </li>
        <li>t.voided = 'F' — excludes voided</li>
        <li>t.posting = 'T' — excludes drafts</li>
        <li>tl.mainline = 'F' — line items only, not headers</li>
        <li>tl.location IN (15, 64) — Jurupa CA + March Products Texas</li>
      </ul>

      <h4>What this captures (and what it still doesn't)</h4>
      <ul>
        <li>
          ✓ Year-over-year growth or decline, per SKU and aggregate (TTM YoY)
        </li>
        <li>✓ Single-year baseline noise (averaged across three years)</li>
        <li>✓ Seasonality — every comparison is the same calendar window</li>
        <li>✓ Returns / credits (netted in via the sign convention)</li>
        <li>
          ✗ <b>Shape inside the window</b> — it predicts one 90-day lump, not a
          curve, so it can't say August is heavier than October
        </li>
        <li>
          ✗ <b>Arrival timing inside Total Supply</b> — every open PO counts in
          full, whether it lands next week or next May. The <b>PO arrives</b>{" "}
          column exposes this (red when it falls outside the window) and the{" "}
          <code>expedite?</code> tag catches the worst of it, but the supply
          total itself is still timing-blind
        </li>
        <li>
          ✗ <b>Open sales orders</b> not yet invoiced — booked-but-uninvoiced
          demand is invisible; this is purely historical
        </li>
        <li>
          ✗ <b>One-offs / outliers</b> — a jumbo order three years ago still
          lifts the baseline by a third of itself, indefinitely. No outlier
          rejection
        </li>
        <li>
          ✗ <b>Recency weighting</b> — 3 years ago counts exactly as much as
          last year in the baseline
        </li>
        <li>
          ✗ <b>New SKUs</b> — no history means a zero baseline, so it will never
          suggest ordering one
        </li>
        <li>
          ✗ <b>A sharp recent turn</b> — TTM lags by design (see the growth
          note above)
        </li>
        <li>✗ Promotional effects, price changes, discontinued lines</li>
      </ul>

      <p>
        <b>Months of Cover</b> = Total Supply ÷ (90-day forecast ÷ 3), i.e.
        against a ~30-day rate.
        Returns <code>null</code> (rendered “∞ / n-a”) for SKUs with zero
        baseline demand — no signal from prior years.
      </p>
      <h4>Need to order — the bottom line</h4>
      <p
        style={{
          fontFamily: "'SF Mono',Menlo,Consolas,monospace",
          background: theme.color.surfaceHover,
          padding: "10px 14px",
          borderRadius: 4,
          fontSize: 12.5,
        }}
      >
        Need to order = max(0, 90-day forecast − Total Supply)
        <br />
        &nbsp;&nbsp;where Total Supply = Comp Avail + Comp PO + unallocated
        On&nbsp;Water
      </p>
      <p>
        The actionable answer to “<b>how much do I need to order now?</b>” —
        what the next 90 days are forecast to consume, minus what's on the
        shelf and everything already on order. A red pill means order that many
        units; <code>—</code> means you're covered (the row's Months of cover
        shows by how much). It's the same supply and demand numbers already in
        the row, just differenced, so it never disagrees with them.
      </p>
      <p>
        <b>Two cases where it is deliberately zero</b> even though the
        arithmetic would give a number:
      </p>
      <ul>
        <li>
          <b>Rows tagged <code>expedite?</code></b> — the stock is already
          bought. Ordering more is not the answer; moving it faster is.
        </li>
        <li>
          <b>SKUs the plan is retiring</b> (discontinued or sell-through).
          Running them down is the intended outcome, so the app never asks you
          to buy more. Monitor suppresses these the same way.
        </li>
      </ul>
      <div
        style={{
          background: theme.color.warningFaint,
          border: `1px solid ${theme.color.warningBorder}`,
          padding: "10px 14px",
          borderRadius: 8,
          fontSize: 12.5,
          lineHeight: 1.6,
          color: theme.color.warningText,
        }}
      >
        <b>Timing caveat — this ignores arrival dates, so treat it as a
        floor.</b>{" "}
        Every open PO counts in full, even one landing <i>after</i> the
        90-day window. Today about <b>50,000 of the ~122,000 units on
        order arrive after the window</b>, so for those SKUs the real
        near-term need is <i>higher</i> than this number — it can under-state,
        never over-state. Use the <b>On Water</b> ETA (trustworthy) and{" "}
        <b>Months of cover</b> to sanity-check timing, and the <b>Monitor</b>{" "}
        tab (trailing burn, ≤1 hour stale) for near-term risk. It's also a
        whole-window total, not a day-by-day timeline, and it applies no MOQ,
        pack-size or lead-time judgement — it's a buyer's starting point.
      </div>
      <p>
        <b>CoverBase rows</b> (the synthetic Sunbrella fabric-code rollups) are
        forecast for demand like any other SKU, but their supply can't be
        summed the same way — so Months of Cover and Need to order are both
        reported as <code>null</code> (rendered <code>—</code>) rather than 0
        or ∞. A buy qty measured against unknown supply would read as a huge
        false order.
      </p>
    </ExplainerPanel>
  );
}
